import { MIXPANEL_CONFIG } from '../config/mixpanel';

// Initialize Mixpanel (client-side only)
let isInitialized = false;
let mixpanel = null;

// Dynamically import mixpanel-browser only on client side
if (typeof window !== 'undefined') {
  import('mixpanel-browser').then((mp) => {
    mixpanel = mp.default;
  }).catch((error) => {
    console.error('❌ [MIXPANEL] Failed to load mixpanel-browser:', error);
  });
}

export const initMixpanel = async () => {
  if (typeof window !== 'undefined' && !isInitialized && !mixpanel) {
    try {
      // Wait for mixpanel to load if it hasn't already
      const mp = await import('mixpanel-browser');
      mixpanel = mp.default;
      mixpanel.init(MIXPANEL_CONFIG.token, MIXPANEL_CONFIG.options);
      isInitialized = true;
      console.log('🎯 [MIXPANEL] Initialized successfully');
    } catch (error) {
      console.error('❌ [MIXPANEL] Initialization failed:', error);
    }
  } else if (typeof window !== 'undefined' && mixpanel && !isInitialized) {
    try {
      mixpanel.init(MIXPANEL_CONFIG.token, MIXPANEL_CONFIG.options);
      isInitialized = true;
      console.log('🎯 [MIXPANEL] Initialized successfully');
    } catch (error) {
      console.error('❌ [MIXPANEL] Initialization failed:', error);
    }
  }
};

// Check if we're on the client side
const isClient = () => typeof window !== 'undefined';

// Safe wrapper for Mixpanel calls
const safeMixpanelCall = async (fn) => {
  if (!isClient()) {
    console.log('🔄 [MIXPANEL] Skipping server-side call');
    return;
  }
  
  if (!isInitialized || !mixpanel) {
    await initMixpanel();
  }
  
  if (!mixpanel) {
    console.log('🔄 [MIXPANEL] Mixpanel not available, skipping call');
    return;
  }
  
  try {
    return fn();
  } catch (error) {
    console.error('❌ [MIXPANEL] Error:', error);
  }
};

// Track events
export const trackEvent = async (eventName, properties = {}) => {
  console.log('🎯 [MIXPANEL] Tracking event:', eventName, properties);
  
  await safeMixpanelCall(() => {
    // Detect environment in browser
    const environment = typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'development' : 'production';
    
    mixpanel.track(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
      environment: environment
    });
  });
};

// Identify user
export const identifyUser = async (userId, properties = {}) => {
  console.log('👤 [MIXPANEL] Identifying user:', userId);
  
  await safeMixpanelCall(() => {
    mixpanel.identify(userId);
    if (Object.keys(properties).length > 0) {
      mixpanel.people.set(properties);
    }
  });
};

// Set user properties
export const setUserProperties = async (properties) => {
  console.log('👤 [MIXPANEL] Setting user properties:', properties);
  
  await safeMixpanelCall(() => {
    mixpanel.people.set(properties);
  });
};

// Track page view
export const trackPageView = async (pageName, properties = {}) => {
  await trackEvent(MIXPANEL_CONFIG.events.PAGE_VIEWED, {
    page_name: pageName,
    ...properties
  });
};

// Specific event tracking functions
export const trackAppInstalled = async (shopData) => {
  await trackEvent(MIXPANEL_CONFIG.events.APP_INSTALLED, {
    shop_name: shopData.shopName,
    shop_domain: shopData.shop,
    owner_name: shopData.shopOwnerName,
    email: shopData.email,
    install_time: new Date().toISOString()
  });
  
  // Set user properties
  await identifyUser(shopData.shop, {
    [MIXPANEL_CONFIG.userProperties.SHOP_NAME]: shopData.shopName,
    [MIXPANEL_CONFIG.userProperties.SHOP_DOMAIN]: shopData.shop,
    [MIXPANEL_CONFIG.userProperties.CURRENT_PLAN]: 'free',
    [MIXPANEL_CONFIG.userProperties.INSTALL_DATE]: new Date().toISOString()
  });
};

export const trackAppUninstalled = async (shopData) => {
  await trackEvent(MIXPANEL_CONFIG.events.APP_UNINSTALLED, {
    shop_name: shopData.shopName,
    shop_domain: shopData.shop,
    current_plan: shopData.currentPlan,
    uninstall_time: new Date().toISOString()
  });
};

export const trackPlanChange = async (eventType, planData) => {
  const eventName = eventType === 'upgrade' ? MIXPANEL_CONFIG.events.PLAN_UPGRADED :
                   eventType === 'downgrade' ? MIXPANEL_CONFIG.events.PLAN_DOWNGRADED :
                   MIXPANEL_CONFIG.events.PLAN_CANCELLED;
  
  await trackEvent(eventName, {
    shop_name: planData.shopName,
    shop_domain: planData.shop,
    previous_plan: planData.previousPlan,
    new_plan: planData.newPlan,
    change_time: planData.upgradeTime || new Date().toISOString()
  });
  
  // Update user properties
  await setUserProperties({
    [MIXPANEL_CONFIG.userProperties.CURRENT_PLAN]: planData.newPlan
  });
};

export const trackDynamicBlockAction = async (action, blockData) => {
  const eventMap = {
    created: MIXPANEL_CONFIG.events.DYNAMIC_BLOCK_CREATED,
    updated: MIXPANEL_CONFIG.events.DYNAMIC_BLOCK_UPDATED,
    deleted: MIXPANEL_CONFIG.events.DYNAMIC_BLOCK_DELETED,
    toggled: MIXPANEL_CONFIG.events.DYNAMIC_BLOCK_TOGGLED
  };
  
  await trackEvent(eventMap[action], {
    block_id: blockData.id,
    block_type: blockData.type,
    block_name: blockData.name,
    is_active: blockData.isActive,
    conditions_count: blockData.conditions?.length || 0
  });
};

export const trackError = async (error, context = {}) => {
  await trackEvent(MIXPANEL_CONFIG.events.ERROR_OCCURRED, {
    error_message: error.message,
    error_stack: error.stack,
    error_name: error.name,
    context: JSON.stringify(context),
    timestamp: new Date().toISOString()
  });
};

// Export the config for use in other files
export { MIXPANEL_CONFIG };
